import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  CheckCircle2,
  XCircle,
  Clock,
  History,
  AlertTriangle,
  HelpCircle,
  ExternalLink
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { runEndpoint } from '@/services/flow';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { DataElement, ElementVersion, ElementVersionProcessingRule, ProcessingRule, ReferenceList } from '@/entities/all';
import FieldChangeHistory from './FieldChangeHistory';

export default function RecordFieldDetails({ dataSourceId, datePath, recordKey, recordIndex }) {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedField, setSelectedField] = useState(null);
  const [elementInfo, setElementInfo] = useState(null);
  const [elementInfoLoading, setElementInfoLoading] = useState(false);

  // Support both recordKey (preferred) and recordIndex (backward compatibility)
  const targetKey = recordKey !== undefined && recordKey !== null ? recordKey : recordIndex;

  useEffect(() => {
    if (dataSourceId && datePath && targetKey !== undefined) {
      loadFieldDetails();
    }
  }, [dataSourceId, datePath, targetKey]);

  const handleElementClick = async (canonicalName) => {
    setElementInfoLoading(true);
    setElementInfo({ canonicalName, element: null, processingRules: [] });

    try {
      const elements = await DataElement.filter({ canonical_name: canonicalName });
      if (elements && elements.length > 0) {
        const element = elements[0];

        // Fetch current version to get processing rules
        const versions = await ElementVersion.filter({ element_id: element.id });
        const currentVersion = versions.find(v => v.version_number === element.current_version_number) || versions[0];

        // Fetch processing rules for this version
        let processingRules = [];
        let referenceList = null;
        if (currentVersion) {
          try {
            // Get junction table entries
            const ruleLinks = await ElementVersionProcessingRule.filter({
              element_version_id: currentVersion.id
            }, 'execution_order');

            // Fetch actual processing rules by ID
            if (ruleLinks && ruleLinks.length > 0) {
              const rules = await Promise.all(
                ruleLinks.map(rl => ProcessingRule.filter({ id: rl.processing_rule_id }))
              );
              processingRules = rules.flat().filter(Boolean);
            }

            // Fetch reference list if exists
            if (currentVersion.reference_list_id) {
              const refLists = await ReferenceList.filter({ id: currentVersion.reference_list_id });
              referenceList = refLists && refLists.length > 0 ? refLists[0] : null;
            }
          } catch (e) {
            console.log('Could not fetch processing rules or reference list:', e);
          }
        }

        setElementInfo({
          canonicalName,
          element,
          currentVersion,
          processingRules,
          referenceList
        });
      } else {
        setElementInfo({ canonicalName, element: null, processingRules: [], notFound: true });
      }
    } catch (error) {
      console.error('Error finding data element:', error);
      toast.error('Failed to find data element');
      setElementInfo(null);
    } finally {
      setElementInfoLoading(false);
    }
  };

  const loadFieldDetails = async () => {
    setIsLoading(true);
    try {
      const response = await runEndpoint('cortex.get_record_field_details', {
        dataSourceId: parseInt(dataSourceId),
        datePath,
        recordKey: targetKey
      });

      if (response?.error) {
        toast.error(response.error);
        setData(null);
      } else {
        setData(response);
      }
    } catch (error) {
      console.error('Error loading field details:', error);
      toast.error('Failed to load field details');
      setData(null);
    } finally {
      setIsLoading(false);
    }
  };

  const getScoreColor = (score, max) => {
    const percentage = (score / max) * 100;
    if (percentage >= 75) return 'bg-green-100 text-green-800';
    if (percentage >= 50) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const getDaysColor = (days) => {
    if (days < 4) return 'text-green-600';
    if (days < 8) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getCategoryIcon = (category, isValid) => {
    if (category === 'validDataElements') {
      return <CheckCircle2 className="h-4 w-4 text-green-500" />;
    }
    if (category === 'invalidDataElements') {
      return <XCircle className="h-4 w-4 text-red-500" />;
    }
    return <AlertTriangle className="h-4 w-4 text-slate-400" />;
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-20" />
        <Skeleton className="h-40" />
      </div>
    );
  }

  if (!data || !data.fields) {
    return (
      <div className="text-center py-10 text-slate-500">
        No field details available
      </div>
    );
  }

  const { fields, recordScores } = data;

  // Group fields by category
  const validFields = fields.filter(f => f.category === 'validDataElements');
  const invalidFields = fields.filter(f => f.category === 'invalidDataElements');
  const sourceFields = fields.filter(f => f.category === 'sourceFields');

  return (
    <div className="space-y-6">
      {/* Record Summary */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
            <div>
              <p className="text-sm text-slate-500">Accuracy</p>
              <p className="text-2xl font-bold">{recordScores.record_accuracy_score}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Completeness</p>
              <p className="text-2xl font-bold">{recordScores.record_completeness_score}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Timeliness</p>
              <p className="text-2xl font-bold">{recordScores.record_timeliness_score}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Total ACT</p>
              <p className="text-2xl font-bold text-blue-600">{recordScores.record_total_act_score}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Fields</p>
              <p className="text-2xl font-bold">{recordScores.field_count}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Valid Data Elements */}
      {validFields.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-green-500" />
            Validated Fields ({validFields.length})
          </h3>
          <FieldsTable
            fields={validFields}
            getScoreColor={getScoreColor}
            getDaysColor={getDaysColor}
            getCategoryIcon={getCategoryIcon}
            onViewHistory={setSelectedField}
            onElementClick={handleElementClick}
          />
        </div>
      )}

      {/* Invalid Data Elements */}
      {invalidFields.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
            <XCircle className="h-4 w-4 text-red-500" />
            Invalid Fields ({invalidFields.length})
          </h3>
          <FieldsTable
            fields={invalidFields}
            getScoreColor={getScoreColor}
            getDaysColor={getDaysColor}
            getCategoryIcon={getCategoryIcon}
            onViewHistory={setSelectedField}
            onElementClick={handleElementClick}
          />
        </div>
      )}

      {/* Unmapped Source Fields */}
      {sourceFields.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-slate-400" />
            Unmapped Source Fields ({sourceFields.length})
          </h3>
          <FieldsTable
            fields={sourceFields}
            getScoreColor={getScoreColor}
            getDaysColor={getDaysColor}
            getCategoryIcon={getCategoryIcon}
            onViewHistory={setSelectedField}
            onElementClick={handleElementClick}
          />
        </div>
      )}

      {/* Field Change History Dialog */}
      <Dialog open={selectedField !== null} onOpenChange={() => setSelectedField(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Change History: {selectedField}</DialogTitle>
          </DialogHeader>
          {selectedField && (
            <FieldChangeHistory
              dataSourceId={dataSourceId}
              fieldName={selectedField}
              recordKey={String(targetKey)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Element Info Dialog */}
      <Dialog open={elementInfo !== null} onOpenChange={() => setElementInfo(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Data Element: {elementInfo?.canonicalName}</DialogTitle>
          </DialogHeader>
          {elementInfoLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-20 w-full" />
            </div>
          ) : elementInfo?.notFound ? (
            <p className="text-slate-500">Data element not found in dictionary.</p>
          ) : elementInfo?.element ? (
            <div className="space-y-4">
              {/* Link to element */}
              <div>
                <Link
                  to={createPageUrl('element-details') + `?id=${elementInfo.element.id}`}
                  target="_blank"
                  className="text-blue-600 hover:underline flex items-center gap-1"
                >
                  Open Data Element Details
                  <ExternalLink className="h-4 w-4" />
                </Link>
              </div>

              {/* Reference List */}
              {elementInfo.referenceList && (
                <div>
                  <h4 className="text-sm font-medium text-slate-700 mb-2">Reference List</h4>
                  <div className="border rounded-lg p-3">
                    <Link
                      to={createPageUrl('reference-list-values') + `?id=${elementInfo.referenceList.id}`}
                      target="_blank"
                      className="font-medium text-blue-600 hover:underline flex items-center gap-1"
                    >
                      {elementInfo.referenceList.list_name}
                      <ExternalLink className="h-3 w-3" />
                    </Link>
                    {elementInfo.referenceList.description && (
                      <p className="text-sm text-slate-500 mt-1">{elementInfo.referenceList.description}</p>
                    )}
                    {elementInfo.referenceList.total_values && (
                      <p className="text-xs text-slate-400 mt-1">{elementInfo.referenceList.total_values} values</p>
                    )}
                  </div>
                </div>
              )}

              {/* Processing Rules */}
              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-2">Processing Rules</h4>
                {elementInfo.processingRules && elementInfo.processingRules.length > 0 ? (
                  <div className="border rounded-lg divide-y">
                    {elementInfo.processingRules.map((rule, idx) => (
                      <div key={rule.id || idx} className="p-3">
                        <div className="flex items-center justify-between">
                          <Link
                            to={createPageUrl('processing-rules') + `/${rule.id}`}
                            target="_blank"
                            className="font-medium text-blue-600 hover:underline flex items-center gap-1"
                          >
                            {rule.rule_name}
                            <ExternalLink className="h-3 w-3" />
                          </Link>
                          <Badge variant="outline" className="text-xs">
                            {rule.rule_language || 'javascript'}
                          </Badge>
                        </div>
                        {rule.description && (
                          <p className="text-sm text-slate-500 mt-1">{rule.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : !elementInfo.referenceList ? (
                  <p className="text-sm text-slate-500">No processing rules configured.</p>
                ) : null}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const TableHeaderWithTooltip = ({ children, tooltip, className = "" }) => (
  <TableHead className={className}>
    <div className="flex items-center gap-1 justify-center">
      {children}
      {tooltip && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <HelpCircle className="h-3 w-3 text-slate-400 cursor-help" />
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">
              <p className="text-xs">{tooltip}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  </TableHead>
);

function FieldsTable({ fields, getScoreColor, getDaysColor, getCategoryIcon, onViewHistory, onElementClick }) {
  return (
    <div className="border rounded-lg overflow-hidden overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Field</TableHead>
            <TableHeaderWithTooltip tooltip="Original value from the data source before any processing or transformation.">Raw Value</TableHeaderWithTooltip>
            <TableHeaderWithTooltip tooltip="Value after processing, validation, and transformation rules have been applied.">Processed Value</TableHeaderWithTooltip>
            <TableHeaderWithTooltip className="text-center" tooltip="Date when this field value was last changed.">Date Changed</TableHeaderWithTooltip>
            <TableHeaderWithTooltip className="text-center" tooltip="1 if field value is valid and passes validation, 0 otherwise.">Accuracy</TableHeaderWithTooltip>
            <TableHeaderWithTooltip className="text-center" tooltip="0-3 score based on value presence (0-1) plus importance weight (0-2).">Completeness</TableHeaderWithTooltip>
            <TableHeaderWithTooltip className="text-center" tooltip="0-4 score based on data age (0-2) plus time-sensitivity weight (0-2).">Timeliness</TableHeaderWithTooltip>
            <TableHeaderWithTooltip className="text-center" tooltip="Sum of Accuracy + Completeness + Timeliness. Max is 8.">Total ACT</TableHeaderWithTooltip>
            <TableHead className="w-20">History</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {fields.map((field, idx) => (
            <TableRow key={idx} className={field.total_act_score < 4 ? 'bg-red-50' : ''}>
              <TableCell className="font-medium">
                <div className="flex items-center gap-2">
                  {getCategoryIcon(field.category, field.isValid)}
                  <div>
                    {field.category !== 'sourceFields' ? (
                      <button
                        onClick={() => onElementClick(field.fieldName)}
                        className="text-blue-600 hover:underline flex items-center gap-1 text-left"
                        title="View data element validation rules"
                      >
                        {field.fieldName}
                        <ExternalLink className="h-3 w-3" />
                      </button>
                    ) : (
                      <div>{field.fieldName}</div>
                    )}
                    {field.sourceFieldName && field.sourceFieldName !== field.fieldName && (
                      <div className="text-xs text-slate-400">{field.sourceFieldName}</div>
                    )}
                  </div>
                </div>
              </TableCell>
              <TableCell className="max-w-[150px]">
                <span className="text-slate-600 truncate block" title={field.rawValue}>
                  {field.rawValue || <span className="text-slate-400 italic">empty</span>}
                </span>
              </TableCell>
              <TableCell className="max-w-[150px]">
                {field.rawValue !== field.processedValue ? (
                  <span className="text-blue-600 font-medium truncate block" title={field.processedValue}>
                    {field.processedValue || <span className="text-slate-400 italic">empty</span>}
                  </span>
                ) : (
                  <span className="text-slate-400 italic">unchanged</span>
                )}
              </TableCell>
              <TableCell className="text-center text-xs text-slate-500 whitespace-nowrap">
                {field.date_changed ? field.date_changed.replace(/year=|month=|day=/g, '').replace(/\//g, '-') : 'N/A'}
              </TableCell>
              <TableCell className="text-center">
                {field.accuracy_reason ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Badge className={`${getScoreColor(field.accuracy_score, 1)} cursor-help`}>
                          {field.accuracy_score}
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs">
                        <p className="text-xs">{field.accuracy_reason}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : (
                  <Badge className={getScoreColor(field.accuracy_score, 1)}>
                    {field.accuracy_score}
                  </Badge>
                )}
              </TableCell>
              <TableCell className="text-center">
                {field.completeness_reason ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Badge className={`${getScoreColor(field.completeness_score, 3)} cursor-help`}>
                          {field.completeness_score}
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs">
                        <p className="text-xs">{field.completeness_reason}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : (
                  <Badge className={getScoreColor(field.completeness_score, 3)}>
                    {field.completeness_score}
                  </Badge>
                )}
              </TableCell>
              <TableCell className="text-center">
                <Badge className={getScoreColor(field.timeliness_score, 4)}>
                  {field.timeliness_score}
                </Badge>
              </TableCell>
              <TableCell className="text-center">
                <Badge className={getScoreColor(field.total_act_score, 8)}>
                  {field.total_act_score}
                </Badge>
              </TableCell>
              <TableCell>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onViewHistory(field.fieldName)}
                >
                  <History className="h-4 w-4" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
